import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Lightbulb, Sparkles, Bug, User } from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { PageTransition } from '../../components/animations/PageTransition';
import { RichTextEditor } from '../../components/editor';
import { REQUEST_TYPES, REQUEST_PRIORITIES } from '@yoda/shared';
import { useUIStore } from '../../stores/uiStore';

interface Project {
  id: string;
  name: string;
  organizationId: string;
}

interface AdminUser {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
}

const typeIcons = {
  idea: Lightbulb,
  feature: Sparkles,
  bug: Bug,
};

export function NewRequestPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedProjectId = searchParams.get('projectId');
  const { setSidebarCollapsed } = useUIStore();

  const [projects, setProjects] = useState<Project[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    projectId: preselectedProjectId || '',
    title: '',
    descriptionHtml: '',
    type: 'feature' as 'idea' | 'feature' | 'bug',
    priority: '' as '' | 'low' | 'medium' | 'high' | 'critical',
    assignedTo: '' as string,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Collapse sidebar on mount, restore on unmount
  useEffect(() => {
    setSidebarCollapsed(true);
    return () => setSidebarCollapsed(false);
  }, [setSidebarCollapsed]);

  useEffect(() => {
    fetchProjects();
    fetchAdminUsers();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await api.get<Project[]>('/projects');
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const response = await api.get<AdminUser[]>('/users?role=admin');
      setAdminUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch admin users:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!form.projectId) {
      setErrors({ projectId: 'Please select a project' });
      return;
    }
    if (!form.title.trim()) {
      setErrors({ title: 'Title is required' });
      return;
    }

    setLoading(true);
    try {
      const response = await api.post<{ id: string }>('/requests', {
        projectId: form.projectId,
        title: form.title,
        descriptionHtml: form.descriptionHtml || undefined,
        type: form.type,
        priority: form.priority || undefined,
        assignedTo: form.assignedTo || undefined,
      });

      const newId = response.data?.id;
      if (newId) {
        navigate(`/requests/${newId}`);
      }
    } catch (error: any) {
      setErrors({ submit: error.message || 'Failed to create request' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTransition>
      <div className="flex-1 flex flex-col min-h-0">
        {/* Header Bar */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-xl hover:bg-bg-secondary transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold">New Request</h1>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 grid grid-cols-1 lg:grid-cols-6 gap-6 min-h-0">
          {/* Left Column - Title + Editor (5/6) */}
          <div className="lg:col-span-5 flex flex-col min-h-0 order-2 lg:order-1">
            {/* Title */}
            <div className="mb-3">
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Request title..."
                className="w-full px-4 py-3 bg-bg-secondary border border-border rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-accent/50"
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title}</p>
              )}
            </div>
            {/* Editor */}
            <div className="flex-1 min-h-0">
              <RichTextEditor
                content={form.descriptionHtml}
                onChange={(html) => setForm({ ...form, descriptionHtml: html })}
                placeholder="Describe the request in detail. You can paste images and annotate them."
                className="h-full"
              />
            </div>
          </div>

          {/* Right Column - Form Controls (1/6) */}
          <div className="lg:col-span-1 space-y-4 order-1 lg:order-2">
            {/* Project Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Project</label>
              <select
                value={form.projectId}
                onChange={(e) => setForm({ ...form, projectId: e.target.value })}
                className="w-full px-3 py-2 bg-bg-secondary border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
              >
                <option value="">Select...</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
              {errors.projectId && (
                <p className="text-red-500 text-sm mt-1">{errors.projectId}</p>
              )}
            </div>

            {/* Type Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Type</label>
              <div className="space-y-2">
                {(Object.entries(REQUEST_TYPES) as [keyof typeof REQUEST_TYPES, typeof REQUEST_TYPES[keyof typeof REQUEST_TYPES]][]).map(([key, value]) => {
                  const Icon = typeIcons[key];
                  const isSelected = form.type === key;

                  return (
                    <motion.button
                      key={key}
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setForm({ ...form, type: key })}
                      className={`w-full p-2.5 rounded-xl border-2 transition-colors flex items-center gap-2 ${
                        isSelected
                          ? 'border-accent bg-accent/10'
                          : 'border-border bg-bg-secondary hover:border-border/80'
                      }`}
                    >
                      <div
                        className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: value.bgColor }}
                      >
                        <Icon className="w-3.5 h-3.5" style={{ color: value.color }} />
                      </div>
                      <span className="font-medium text-sm truncate">{value.label}</span>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium mb-2">Priority</label>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, priority: '' })}
                  className={`px-2.5 py-1 rounded-lg border text-xs transition-colors ${
                    !form.priority
                      ? 'border-accent bg-accent/10 text-accent'
                      : 'border-border bg-bg-secondary hover:border-border/80'
                  }`}
                >
                  None
                </button>
                {(Object.entries(REQUEST_PRIORITIES) as [keyof typeof REQUEST_PRIORITIES, typeof REQUEST_PRIORITIES[keyof typeof REQUEST_PRIORITIES]][]).map(([key, value]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setForm({ ...form, priority: key })}
                    className={`px-2.5 py-1 rounded-lg border text-xs transition-colors ${
                      form.priority === key
                        ? 'border-accent bg-accent/10'
                        : 'border-border bg-bg-secondary hover:border-border/80'
                    }`}
                    style={
                      form.priority === key
                        ? { borderColor: value.color, backgroundColor: value.bgColor }
                        : {}
                    }
                  >
                    <span style={form.priority === key ? { color: value.color } : {}}>
                      {value.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Assignee Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Assign To</label>
              <select
                value={form.assignedTo}
                onChange={(e) => setForm({ ...form, assignedTo: e.target.value })}
                className="w-full px-3 py-2 bg-bg-secondary border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
              >
                <option value="">Unassigned</option>
                {adminUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-text-muted mt-1">
                Assign a team member to handle this request
              </p>
            </div>

            {/* Error Message */}
            {errors.submit && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-sm">
                {errors.submit}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-2 pt-2">
              <Button type="submit" isLoading={loading} className="w-full">
                Create
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(-1)}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </div>
    </PageTransition>
  );
}
